<script setup lang="ts">
import { CirclePlus, Fold } from "@element-plus/icons-vue";
import { computed, onMounted, ref } from "vue";
import { Slotted } from "~/types/Slotted";

import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";

import ChecklistEditor from "~/Compliance/components/ChecklistEditor.vue";
import { Checklist, SysOrgRoleName } from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";

const myInfoStore = useMyInfoStore();

const loading = ref(true);
const editChecklistDrawerIsOpen = ref<boolean>(false);
const isCreatingChecklist = ref(true);

/*** for querying/filtering the listing ***/
// Need to fix
const complianceQueryInput = ref({
  type: "",
  category: "",
  status: "",
  search: "",
});

const hasSetupBasicRole = myInfoStore.currentSysOrg.roles.includes(
  SysOrgRoleName.SETUP_BASIC,
);

const selectedChecklistDefinition = ref<Checklist>();
const checklistDefinitionList = ref<Checklist[]>([]);

const refreshChecklistDefinitions = async () => {
  await apiClient.checklistDefinition
    .getAllChecklistDefinitions(myInfoStore.currentSysOrgGuid)
    .then((res) => {
      checklistDefinitionList.value = res;
      loading.value = false;
    })
    .catch(showError);
};

const checklistListFiltered = computed(() => {
  return checklistDefinitionList.value.filter((item) => {
    return item.name
      ?.toLowerCase()
      .includes(complianceQueryInput.value.search.toLowerCase());
  });
});

const handleCreateChecklistDefinition = () => {
  editChecklistDrawerIsOpen.value = true;
  selectedChecklistDefinition.value = {
    guid: "new",
    sysOrgGuid: emptyGuid,
    entityDefinitionGuid: emptyGuid,
    name: "",
    itemsInChecklists: [],
  };
  isCreatingChecklist.value = true;
};

const handleGetChecklistInfo = async (item: string | undefined) => {
  const res = await apiClient.checklistDefinition.getChecklistDefinition(
    item!,
    myInfoStore.currentSysOrgGuid,
  );
  selectedChecklistDefinition.value = res;
  return selectedChecklistDefinition;
};

const showItem = async (checklist: Checklist) => {
  isCreatingChecklist.value = false;
  await handleGetChecklistInfo(checklist.guid);
  editChecklistDrawerIsOpen.value = true;
};

const closeDrawer = () => {
  editChecklistDrawerIsOpen.value = false;
  selectedChecklistDefinition.value = undefined;
};

onMounted(async () => {
  refreshChecklistDefinitions();
});
</script>

<template>
  <ChecklistEditor
    v-if="selectedChecklistDefinition?.guid"
    :key="selectedChecklistDefinition.guid"
    v-model="editChecklistDrawerIsOpen"
    :checklist-dto="selectedChecklistDefinition"
    :is-creating-checklist="isCreatingChecklist"
    @close-drawer="closeDrawer"
    @added="refreshChecklistDefinitions()"
    @updated="refreshChecklistDefinitions()"
  />

  <div class="flexRow">
    <span class="showingInTable">
      Showing {{ checklistListFiltered.length }} of
      {{ checklistDefinitionList.length }}
    </span>
    <RefreshButton @click="refreshChecklistDefinitions" />
    <el-button
      v-if="hasSetupBasicRole"
      class="addButton"
      :icon="CirclePlus"
      type="primary"
      style="font-size: 15px"
      @click="handleCreateChecklistDefinition"
    >
      New Checklist</el-button
    >
  </div>

  <el-table
    :data="checklistListFiltered"
    style="width: 100%"
    row-class-name="tableRow"
  >
    <el-table-column prop="name" label="Checklist Name">
      <template #default="{ row }: Slotted<Checklist>">
        <el-link :icon="Fold" underline="never" @click="showItem(row)">
          {{ row.name }}
        </el-link>
      </template>
    </el-table-column>
  </el-table>
</template>

<style lang="less"></style>
